import { z } from 'zod';
import { wordCount } from '../domain/text.js';
export const stockMovementTypeSchema = z.enum([
    'INBOUND_PALLET',
    'INBOUND_DIRECT',
    'CONSUMPTION',
    'ASSEMBLY_IN',
    'DELIVERY_OUT',
    'MANUAL_IN',
    'MANUAL_OUT',
    'ADJUSTMENT',
]);
/**
 * Direct restock (Path B, TRS §6.5) — receive a LIST of items straight into a
 * partner's stock without a pallet. Each line is any SKU (component or product)
 * + a positive qty (Jonas #6.1, 2026-06-19; was a single line).
 */
export const restockLineSchema = z.object({
    skuId: z.string().uuid(),
    qty: z.number().positive().max(1_000_000),
    comment: z
        .string()
        .trim()
        .max(2000)
        .refine((s) => wordCount(s) >= 2, { message: 'comment_too_short' }),
});
export const restockSchema = z.object({
    lines: z.array(restockLineSchema).min(1).max(200),
    /** Omit for direct stock; otherwise receive the entire batch onto this pallet. */
    palletId: z.string().uuid().optional(),
    date: z.coerce.date().optional(),
    photoKey: z.string().trim().max(512).optional(),
});
/** Manual stock adjustment (TRS §6.12). qty is signed; sign determines IN/OUT. */
export const adjustStockSchema = z.object({
    skuId: z.string().uuid(),
    qty: z
        .number()
        .refine((v) => v !== 0, { message: 'qty must be non-zero' })
        .refine((v) => Math.abs(v) <= 1_000_000),
    reason: z.string().trim().min(1).max(2000),
    comment: z.string().trim().max(2000).optional(),
    date: z.coerce.date().optional(),
});
export const inventoryAdjustmentModeSchema = z.enum(['in', 'out', 'actual']);
export const inventoryLocationSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('SHELF') }),
    z.object({ type: z.literal('PALLET'), palletId: z.string().uuid() }),
]);
export const inventoryAdjustmentLineSchema = z
    .object({
    skuId: z.string().uuid(),
    mode: inventoryAdjustmentModeSchema,
    qty: z.number().int().min(0).max(1_000_000),
    /** Omitted only by older clients; new UI always chooses a physical location. */
    location: inventoryLocationSchema.optional(),
})
    .superRefine((line, ctx) => {
    if (line.mode !== 'actual' && line.qty === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['qty'],
            message: 'qty must be positive for manual adjustments',
        });
    }
});
/** Multi-line stocktake / manual inventory adjustment (WMS-75). */
export const inventoryAdjustmentSchema = z
    .object({
    lines: z.array(inventoryAdjustmentLineSchema).min(1).max(200),
    reason: z
        .string()
        .trim()
        .max(2000)
        .refine((s) => wordCount(s) >= 2, { message: 'reason_too_short' }),
})
    .superRefine((input, ctx) => {
    const seen = new Map();
    input.lines.forEach((line, index) => {
        const previous = seen.get(line.skuId) ?? [];
        // Only explicit counts at distinct physical locations may share a SKU.
        // Legacy global counts and manual +/- lines cannot be mixed with them.
        if (previous.some((other) => line.mode !== 'actual' ||
            other.mode !== 'actual' ||
            !line.location ||
            !other.location ||
            (line.location.type === other.location.type &&
                (line.location.type === 'SHELF' ||
                    (other.location.type === 'PALLET' &&
                        line.location.palletId === other.location.palletId))))) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['lines', index, 'skuId'],
                message: 'duplicate or overlapping sku location',
            });
        }
        seen.set(line.skuId, [...previous, line]);
    });
});
/** Unpack-pallet body (Path A, TRS §6.5). One line per SKU contributed. */
export const unpackPalletLineSchema = z.object({
    skuId: z.string().uuid(),
    qty: z.number().positive().max(1_000_000),
    comment: z.string().trim().max(500).optional(),
});
export const unpackPalletSchema = z.object({
    /** The partner whose stock receives the contents. */
    partnerId: z.string().uuid(),
    lines: z.array(unpackPalletLineSchema).min(1).max(200),
    /** Optional atomic move of some just-posted content to the virtual Shelf. */
    shelfLines: z
        .array(z.object({
        skuId: z.string().uuid(),
        qty: z.number().positive().max(1_000_000),
    }))
        .max(200)
        .optional(),
    reason: z.string().trim().max(2000).optional(),
    /** Effective date of the unpack (WMS-16). Defaults to now when omitted. */
    date: z.coerce.date().optional(),
});
/** Apply one checked content list to several partner pallets atomically. */
export const bulkUnpackPalletsSchema = unpackPalletSchema
    .extend({
    palletIds: z.array(z.string().uuid()).min(2).max(100),
})
    .superRefine((data, ctx) => {
    if (new Set(data.palletIds).size !== data.palletIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['palletIds'],
            message: 'palletIds must be unique',
        });
    }
});
//# sourceMappingURL=stock.js.map